import { Error } from "@components/Error";
import { Loader } from "@components/Loader";
import MARC21Preview from "@components/MARC21Preview";
import PropTypes from "prop-types";
import React, { Component } from "react";
import {
  Container,
  Divider,
  Grid,
  Ref,
  Sticky,
  Button,
  Segment,
  Header,
  Icon,
} from "semantic-ui-react";
import { DocumentActionMenu } from "./DocumentActionMenu";
import { DocumentMetadata } from "./DocumentMetadata";
import { DocumentSummary } from "./DocumentSummary";
import { DocumentContent } from "./DocumentContent";
import { DocumentHeader } from "./DocumentHeader";
import { documentApi } from "@api/documents";

export default class DocumentDetails extends Component {
  constructor(props) {
    super(props);
    this.menuRef = React.createRef();
    this.headerRef = React.createRef();

    this.anchors = {
      summaryRef: React.createRef(),
      loanRequestsRef: React.createRef(),
      attachedItemsRef: React.createRef(),
      attachedEItemsRef: React.createRef(),
      seriesRef: React.createRef(),
      relatedRef: React.createRef(),
      borrowingRequestsRef: React.createRef(),
      purchaseOrdersRef: React.createRef(),
      statisticsRef: React.createRef(),
      exportRef: React.createRef(),
    };
  }

  componentDidMount() {
    const { fetchDocumentDetails, match } = this.props;
    fetchDocumentDetails(match.params.documentPid);
  }

  componentDidUpdate(prevProps) {
    const { fetchDocumentDetails, match } = this.props;

    const documentPid = match.params.documentPid;
    const samePidFromRouter =
      prevProps.match.params.documentPid === documentPid;
    if (!samePidFromRouter) {
      fetchDocumentDetails(documentPid);
    }
  }

  handleMARC21Export = async (format) => {
    const { data } = this.props;
    const documentId = data.metadata.pid;

    try {
      const response = await fetch(
        `${documentApi.searchBaseURL}/${documentId}?format=${format}`,
        {
          credentials: "include",
          headers: {
            Accept:
              format === "marcxml"
                ? "application/marcxml+xml"
                : "application/marc",
          },
          cache: "default",
        }
      );

      if (!response.ok && response.status !== 304) {
        throw new Error(
          `Export failed: ${response.status} ${response.statusText}`
        );
      }

      const blob = await response.blob();
      const fileName = this.generateFileName(format, documentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("MARC21 Export Error:", error);
      const exportUrl = `${documentApi.searchBaseURL}/${documentId}?format=${format}`;
      window.open(exportUrl, "_blank");
    }
  };

  generateFileName = (format, documentId) => {
    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:-]/g, "");
    const extension = format === "marcxml" ? "xml" : "mrc";
    return `document_${documentId}_${timestamp}.${extension}`;
  };

  // Render Export Section
  renderExportSection = () => {
    const { data } = this.props;

    if (!data || !data.metadata) {
      return null;
    }

    return (
      <div ref={this.anchors.exportRef} id="document-export">
        <Segment>
          <Header as="h3" dividing>
            <Icon name="download" />
            Export Document
          </Header>

          <p>Export this document's bibliographic record in various formats:</p>

          <Button.Group size="small">
            <MARC21Preview
              documentId={data.metadata.pid}
              trigger={<Button icon="eye" content="Preview MARC21" basic />}
            />

            <Button
              size="mini"
              basic
              icon="download"
              content="Export MARC21"
              title="Export MARC21 binary record"
              onClick={() => this.handleMARC21Export("marc21")}
            />
          </Button.Group>

          <div style={{ marginTop: "10px", fontSize: "0.9em", color: "#666" }}>
            <strong>MARC21 Binary:</strong> Compact binary format for
            professional library management systems
          </div>
        </Segment>
      </div>
    );
  };

  render() {
    const { isLoading, error, data } = this.props;
    return (
      <div ref={this.headerRef}>
        <Container fluid>
          <Loader isLoading={isLoading}>
            <Error error={error}>
              <Sticky
                context={this.headerRef}
                className="solid-background"
                style={{ zIndex: 1, position: "relative" }}
              >
                <Container fluid className="spaced">
                  <DocumentHeader data={data} />
                </Container>
                <Divider />
              </Sticky>
              <Container fluid>
                <Ref innerRef={this.menuRef}>
                  <Grid columns={2}>
                    <Grid.Column width={13}>
                      <Container className="spaced">
                        <Container className="spaced">
                          <div
                            ref={this.anchors.summaryRef}
                            id="document-summary"
                          >
                            <DocumentSummary
                              anchors={this.anchors}
                              document={data}
                            />
                          </div>
                        </Container>
                        <DocumentMetadata anchors={this.anchors} />
                        <DocumentContent anchors={this.anchors} data={data} />

                        {/* Add dedicated export section */}
                        <Container className="spaced">
                          {this.renderExportSection()}
                        </Container>
                      </Container>
                    </Grid.Column>
                    <Grid.Column width={3}>
                      <Sticky context={this.menuRef} offset={200}>
                        <DocumentActionMenu offset={-250} />
                      </Sticky>
                    </Grid.Column>
                  </Grid>
                </Ref>
              </Container>
            </Error>
          </Loader>
        </Container>
      </div>
    );
  }
}

DocumentDetails.propTypes = {
  // Redux
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.object,
  data: PropTypes.object.isRequired,
  fetchDocumentDetails: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      documentPid: PropTypes.string,
    }),
  }).isRequired,
};

DocumentDetails.defaultProps = {
  error: null,
};
